import { CloseOutlined, SwapRightOutlined, CalendarOutlined } from '@ant-design/icons';
import { Button, Form, Select, message, Popover, Popconfirm } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import modal from '~components/modal';
import {
  MoveInDateOptionsKeys,
  MoveOutDateOptionsKeys,
  MoveInDateOptions,
  MoveOutDateOptions,
  DailyPriceTypes,
} from 'common/constants/price-calc-display';
import SelectList from '../select-list';
import { FinalPriceCalculationOptions } from '../index';

const getMutationMoveInDateValue = (moveType, moveSpecificDays, moveRange) => {
  if ([MoveInDateOptionsKeys.EARLIER_THAN].includes(moveType)) {
    return {
      start: undefined,
      end: moveSpecificDays,
    };
  }

  if ([MoveOutDateOptionsKeys.LATER_THAN].includes(moveType)) {
    return {
      start: moveSpecificDays,
      end: undefined,
    };
  }

  if ([MoveInDateOptionsKeys.RANGE, MoveOutDateOptionsKeys.RANGE].includes(moveType)) {
    return {
      start: moveRange[0],
      end: moveRange[1],
    };
  }

  return null;
};

function FinalPriceCalcEditor(props) {
  const { handleCloseModal, form, editingPriceCalculationRule, propertyId } = props;
  const { getFieldDecorator } = form;
  const { t } = useTranslation();

  console.log(editingPriceCalculationRule);

  const [selectedMethod, setSelectedMethod] = useState(
    editingPriceCalculationRule?.calculateMethod
      ? `${editingPriceCalculationRule.calculateMethod}_SETTING`
      : 'BASIC_SETTING',
  );

  const onSubmit = () => {
    form.validateFieldsAndScroll(async err => {
      if (err) {
        message.warn('Please check your input.');
        return;
      }
      const formData = form.getFieldsValue();

      const moveInDateValue = getMutationMoveInDateValue(
        formData.moveInType,
        formData.moveInSpecificDays,
        formData.moveInRange,
      );

      const moveOutDateValue = getMutationMoveInDateValue(
        formData.moveOutType,
        formData.moveOutSpecificDays,
        formData.moveOutRange,
      );

      const mutationVariables = {
        // the next values can't be changed here.
        dailyPriceType: editingPriceCalculationRule?.dailyPriceType,
        customLength: editingPriceCalculationRule?.customLength,
        //  can't be changed values end.

        calculateMethod: selectedMethod.replace('_SETTING', ''),
        moveInType: formData.moveInType,
        moveOutType: formData.moveOutType,
        moveInStart: moveInDateValue?.start,
        moveInEnd: moveInDateValue?.end,
        moveOutStart: moveOutDateValue?.start,
        moveOutEnd: moveOutDateValue?.end,
      };

      try {
        handleCloseModal({
          priceCalculationRule: {
            ...(editingPriceCalculationRule
              ? { id: editingPriceCalculationRule?.id }
              : { propertyId }),
            ...mutationVariables,
          },
        });
      } catch (error) {
        message.error(error.toString());
      }
    });
  };

  const selectedMoveIn =
    form.getFieldValue('moveInType') || editingPriceCalculationRule?.moveInType;
  const selectedMoveOut =
    form.getFieldValue('moveOutType') || editingPriceCalculationRule?.moveOutType;

  const shouldDisabledActualDaysOption = moveType =>
    editingPriceCalculationRule?.dailyPriceType === DailyPriceTypes.CALENDAR_MONTH_DAYS &&
    moveType === 'ACTUAL_DAYS';

  return (
    <div className="final-calc-display-editor-modal">
      <div className="modal__header">
        {t(`cms.property.listing_management.price-calc-display.final_price_editor.title`)}
        <Popconfirm
          overlayStyle={{ width: 240 }}
          title={t('cms.properties.edit.leave_alert.content')}
          onConfirm={handleCloseModal}
          okText={t('cms.properties.edit.leave_alert.button.yes')}
          cancelText={t('cms.properties.edit.btn.no')}
          arrowPointAtCenter
          placement="leftTop"
        >
          <CloseOutlined className="modal__close-button" />
        </Popconfirm>
      </div>
      <div className="modal__body">
        <section className="final-calc-display-editor-modal__choose-method">
          <h3>
            {t(
              'cms.property.listing_management.price-calc-display.final_price_editor.choose_method',
            )}
            :
          </h3>

          <SelectList.Group
            value={selectedMethod}
            onChange={method => {
              setSelectedMethod(method);
            }}
          >
            <SelectList.Item
              name={'BASIC_SETTING'}
              label={t(FinalPriceCalculationOptions.BASIC_SETTING.label)}
              des={t(FinalPriceCalculationOptions.BASIC_SETTING.des)}
              renderExtraInfo={
                <span>{t(FinalPriceCalculationOptions.BASIC_SETTING.extraDes[0])}</span>
              }
            />

            <SelectList.Item
              name={'ADVANCED_SETTING'}
              label={t(FinalPriceCalculationOptions.ADVANCED_SETTING.label)}
              des={t(FinalPriceCalculationOptions.ADVANCED_SETTING.des)}
              renderExtraInfo={
                <span>
                  {t(
                    'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.extra',
                  )}
                </span>
              }
              renderSetting={() => {
                return (
                  <>
                    <p>
                      {t(
                        'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.des.1',
                      )}
                    </p>
                    <p>
                      {t(
                        'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.des.2',
                      )}
                    </p>
                    <p>
                      {t(
                        'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.des.3',
                      )}
                    </p>
                    <Form.Item
                      label={
                        <label className="final-calc-display-editor-modal__field-label">
                          {t(
                            'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.move_in',
                          )}
                        </label>
                      }
                      required
                    >
                      {getFieldDecorator('moveInType', {
                        rules: [
                          {
                            required: true,
                            message: t('cms.listing.modal.error_message.can_not_empty'),
                          },
                        ],

                        initialValue: editingPriceCalculationRule?.moveInType || undefined,
                      })(
                        <Select
                          className="final-calc-display-editor-modal__full-width final-calc-display-editor-modal__max-628"
                          placeholder={t(
                            'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.move.placeholder',
                          )}
                          optionLabelProp="label"
                          dropdownClassName="final-calc-display-editor-modal__dropdown"
                        >
                          <Select.OptGroup
                            label={t(
                              'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.charge_as_full_month',
                            )}
                          >
                            {[
                              MoveInDateOptionsKeys.EARLIER_THAN,
                              MoveInDateOptionsKeys.RANGE,
                              MoveInDateOptionsKeys.NO_LIMIT,
                            ].map(option => (
                              <Select.Option
                                key={option}
                                value={option}
                                label={`${t(
                                  'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.charge_as_full_month.full',
                                )} | ${MoveInDateOptions[option]}`}
                              >
                                {MoveInDateOptions[option]}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                          <Select.OptGroup
                            label={t(
                              'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.charge_as_day',
                            )}
                          >
                            {[MoveInDateOptionsKeys.ACTUAL_DAYS].map(option => (
                              <Select.Option
                                //The disable state depends on whether another option has selected the same option
                                disabled={shouldDisabledActualDaysOption(selectedMoveOut)}
                                key={option}
                                value={option}
                                style={{ whiteSpace: 'normal' }}
                                label={`${MoveInDateOptions[option]}`}
                              >
                                {MoveInDateOptions[option]}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                        </Select>,
                      )}
                    </Form.Item>

                    {selectedMoveIn === MoveInDateOptionsKeys.EARLIER_THAN && (
                      <Form.Item
                        label={
                          <label className="final-calc-display-editor-modal__field-label">
                            {t(
                              'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.start_date',
                            )}
                            <span className="final-calc-display-editor-modal__field-label-extra">
                              (
                              {t(
                                'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.dont_include_tip',
                              )}
                              )
                            </span>
                          </label>
                        }
                        required
                      >
                        {getFieldDecorator('moveInSpecificDays', {
                          rules: [
                            {
                              required: true,
                              message: t('cms.listing.modal.error_message.can_not_empty'),
                            },
                          ],
                          initialValue: editingPriceCalculationRule?.moveInEnd,
                        })(
                          <SelectList.ButtonList
                            buttonSameSize
                            options={Array(29)
                              .fill('PLACEHOLDER')
                              .map((_value, index) => ({
                                label: index + 3,
                                value: index + 3,
                              }))}
                          />,
                        )}
                      </Form.Item>
                    )}
                    {selectedMoveIn === MoveInDateOptionsKeys.RANGE && (
                      <Form.Item
                        label={
                          <label className="final-calc-display-editor-modal__field-label">
                            {t(
                              'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.start_range',
                            )}
                            <span className="final-calc-display-editor-modal__field-label-extra">
                              (
                              {t(
                                'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.include_tip',
                              )}
                              )
                            </span>
                          </label>
                        }
                        required
                      >
                        {getFieldDecorator('moveInRange', {
                          rules: [
                            {
                              required: true,
                              message: t('cms.listing.modal.error_message.can_not_empty'),
                            },
                          ],
                          initialValue:
                            typeof editingPriceCalculationRule?.moveInStart === 'number' &&
                            typeof editingPriceCalculationRule?.moveInEnd === 'number'
                              ? [
                                  editingPriceCalculationRule.moveInStart,
                                  editingPriceCalculationRule.moveInEnd,
                                ]
                              : [],
                        })(<NumRangePicker />)}
                      </Form.Item>
                    )}

                    <Form.Item
                      label={
                        <label className="final-calc-display-editor-modal__field-label">
                          {t(
                            'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.move_out',
                          )}
                        </label>
                      }
                      required
                    >
                      {getFieldDecorator('moveOutType', {
                        rules: [
                          {
                            required: true,
                            message: t('cms.listing.modal.error_message.can_not_empty'),
                          },
                        ],
                        initialValue: editingPriceCalculationRule?.moveOutType || undefined,
                      })(
                        <Select
                          className="final-calc-display-editor-modal__full-width final-calc-display-editor-modal__max-628"
                          placeholder={t(
                            'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.move.placeholder',
                          )}
                          optionLabelProp="label"
                        >
                          <Select.OptGroup
                            label={t(
                              'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.charge_as_full_month',
                            )}
                          >
                            {[
                              MoveOutDateOptionsKeys.LATER_THAN,
                              MoveOutDateOptionsKeys.RANGE,
                              MoveOutDateOptionsKeys.NO_LIMIT,
                            ].map(option => (
                              <Select.Option
                                key={option}
                                value={option}
                                label={`${t(
                                  'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.charge_as_full_month.full',
                                )} | ${MoveOutDateOptions[option]}`}
                              >
                                {MoveOutDateOptions[option]}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                          <Select.OptGroup
                            label={t(
                              'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.charge_as_day',
                            )}
                          >
                            {[MoveOutDateOptionsKeys.ACTUAL_DAYS].map(option => (
                              <Select.Option
                                //The disable state depends on whether another option has selected the same option
                                disabled={shouldDisabledActualDaysOption(selectedMoveIn)}
                                style={{ whiteSpace: 'normal' }}
                                key={option}
                                value={option}
                                label={`${MoveOutDateOptions[option]}`}
                              >
                                {MoveOutDateOptions[option]}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                        </Select>,
                      )}
                    </Form.Item>

                    {selectedMoveOut === MoveOutDateOptionsKeys.LATER_THAN && (
                      <Form.Item
                        label={
                          <label className="final-calc-display-editor-modal__field-label">
                            {t(
                              'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.end_date',
                            )}
                            <span className="final-calc-display-editor-modal__field-label-extra">
                              (
                              {t(
                                'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.dont_include_tip',
                              )}
                              )
                            </span>
                          </label>
                        }
                        required
                      >
                        {getFieldDecorator('moveOutSpecificDays', {
                          rules: [
                            {
                              required: true,
                              message: t('cms.listing.modal.error_message.can_not_empty'),
                            },
                          ],
                          initialValue: editingPriceCalculationRule?.moveOutStart,
                        })(
                          <SelectList.ButtonList
                            buttonSameSize
                            options={Array(29)
                              .fill('PLACEHOLDER')
                              .map((_value, index) => ({
                                label: index + 1,
                                value: index + 1,
                              }))}
                          />,
                        )}
                      </Form.Item>
                    )}
                    {selectedMoveOut === MoveOutDateOptionsKeys.RANGE && (
                      <Form.Item
                        label={
                          <label className="final-calc-display-editor-modal__field-label">
                            {t(
                              'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.end_range',
                            )}
                            <span className="final-calc-display-editor-modal__field-label-extra">
                              (
                              {t(
                                'cms.property.listing_management.price-calc-display.final_price_editor.advanced_option.include_tip',
                              )}
                              )
                            </span>
                          </label>
                        }
                        required
                      >
                        {getFieldDecorator('moveOutRange', {
                          rules: [
                            {
                              required: true,
                              message: t('cms.listing.modal.error_message.can_not_empty'),
                            },
                          ],
                          initialValue:
                            typeof editingPriceCalculationRule?.moveOutStart === 'number' &&
                            typeof editingPriceCalculationRule?.moveOutEnd === 'number'
                              ? [
                                  editingPriceCalculationRule.moveOutStart,
                                  editingPriceCalculationRule.moveOutEnd,
                                ]
                              : [],
                        })(<NumRangePicker />)}
                      </Form.Item>
                    )}
                  </>
                );
              }}
            />
          </SelectList.Group>
        </section>
      </div>

      <div className="modal__footer">
        <Popconfirm
          overlayStyle={{ width: 240 }}
          title={t('cms.properties.edit.leave_alert.content')}
          onConfirm={handleCloseModal}
          okText={t('cms.properties.edit.leave_alert.button.yes')}
          cancelText={t('cms.properties.edit.btn.no')}
          placement="topRight"
        >
          <Button size="large">{t('cms.referral.promotion.modal.btn.cancel')}</Button>
        </Popconfirm>

        <Button type="primary" size="large" onClick={onSubmit}>
          {t('cms.properties.edit.btn.confirm')}
        </Button>
      </div>
    </div>
  );
}

function NumRangePicker({ value, onChange }) {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [selectedRange, setSelectedRange] = useState(value || []);
  const [activeRange, setActiveRange] = useState([]);

  const handleRangeSelect = v => {
    if (selectedRange.length === 2) {
      return setSelectedRange([v]);
    }
    if (selectedRange.length === 0) {
      return setSelectedRange([v]);
    }
    if (selectedRange.length === 1 && !selectedRange.includes(v)) {
      const newSelectedRange = selectedRange.concat(v).sort((a, b) => a - b);
      setActiveRange(generateRangeNum(newSelectedRange[0], newSelectedRange[1]));
      return setSelectedRange(newSelectedRange);
    }
    setSelectedRange([v]);
  };

  const handleOnSubmit = () => {
    if (selectedRange.length === 2) {
      onChange(selectedRange);
      setPopoverVisible(false);
    }
  };

  const handleClickOutside = event => {
    const wrapper = document.querySelector('.num-range-picker');
    const popover = document.querySelector('.num-range-picker__popover');
    if (!wrapper || !popover) return;
    if (!wrapper.contains(event.target)) {
      if (!popover.contains(event.target)) {
        setPopoverVisible(false);
      }
    }
  };

  const handleHoverEnter = value => {
    if (selectedRange.length === 1) {
      setActiveRange(generateRangeNum(selectedRange[0], value));
    }
  };

  const handleReset = () => {
    setSelectedRange([]);
    setActiveRange([]);
    onChange([]);
  };

  React.useEffect(() => {
    document
      .querySelector('.final-calc-display-editor-modal')
      ?.addEventListener('mousedown', handleClickOutside);

    return () => {
      document
        .querySelector('.final-calc-display-editor-modal')
        ?.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="num-range-picker">
      <Popover
        overlayClassName="num-range-picker__popover"
        content={
          <div>
            <div className="num-range-picker__range-zone">
              {Array(31)
                .fill('PLACEHOLDER')
                .map((_value, index) => (
                  <button
                    onMouseEnter={() => handleHoverEnter(index + 1)}
                    className={classNames('num-range-picker__range-item', {
                      'num-range-picker__range-item--selected': selectedRange.includes(index + 1),
                      'num-range-picker__range-item--in-range': activeRange.includes(index + 1),
                    })}
                    key={index}
                    onClick={() => handleRangeSelect(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
            </div>
            <div className="num-range-picker__menu">
              <Button
                size="small"
                type="link"
                className="num-range-picker__menu__button--reset"
                onClick={handleReset}
              >
                Reset
              </Button>
              <Button
                size="small"
                type="primary"
                onClick={handleOnSubmit}
                disabled={selectedRange?.length !== 2}
              >
                OK
              </Button>
            </div>
          </div>
        }
        visible={popoverVisible}
        placement="bottomLeft"
      >
        <div
          className={classNames('num-range-picker__fake-input', {
            'num-range-picker__fake-input--active': popoverVisible,
          })}
          onClick={() => setPopoverVisible(true)}
        >
          <span
            className={classNames('num-range-picker__fake-input__input', {
              'num-range-picker__fake-input__num': typeof value?.[0] === 'number',
              'num-range-picker__fake-input__placeholder': typeof value?.[0] !== 'number',
            })}
          >
            {value?.[0] || 'From a date'}
          </span>
          <SwapRightOutlined className="num-range-picker__fake-input__middle-icon" />
          <span
            className={classNames('num-range-picker__fake-input__input', {
              'num-range-picker__fake-input__num': typeof value?.[1] === 'number',
              'num-range-picker__fake-input__placeholder': typeof value?.[1] !== 'number',
            })}
          >
            {value?.[1] || 'To a date'}
          </span>
          <CalendarOutlined className="num-range-picker__fake-input__end-icon" />
        </div>
      </Popover>
    </div>
  );
}

/**
 * Generates an array of numbers within a given range, excluding the start and end values.
 *
 * @param {number} start - The starting value of the range.
 * @param {number} end - The ending value of the range.
 * @return {number[]} An array of numbers within the given range, excluding the start and end values.
 */
function generateRangeNum(start, end) {
  const [min, max] = start < end ? [start, end] : [end, start];
  const array = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  array.shift();
  array.pop();
  return array || [];
}

FinalPriceCalcEditor.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
};

FinalPriceCalcEditor.defaultProps = {
  handleCloseModal: () => {},
};

export default Form.create()(
  modal(
    {
      className: 'final-calc-display-editor-modal-container',
      useDefaultStyle: true,
    },
    true,
  )(FinalPriceCalcEditor),
);
